import axios from 'axios';
import imageCompression from 'browser-image-compression';

import { getCurrentUser } from '../utils';
import buffer from 'buffer';
import LocalPreferenceService from 'services/offlineServices/localPreference.service';
import { MODULE_IDS } from '../constants';

const getSignedUrlFromS3 = (contentType, fileName, moduleName, companyId) => {
  if (!contentType || !moduleName || !fileName || !companyId) {
    throw new Error('Mandatory fields missing');
  }
  return axios({
    method: 'get',
    url: process.env.REACT_APP_S3_UPLOADER_API_GATEWAY,
    params: { contentType, fileName, companyId, moduleName }
  })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      return Promise.reject(error);
    });
};

const uploadBase64DataURL = async (
  dataURL = '',
  contentType,
  fileName,
  moduleName,
  companyId = getCurrentUser().companyId
) => {
  try {
    if (!dataURL || !contentType || !moduleName || !fileName || !companyId) {
      throw new Error('Mandatory fields missing');
    }
    if (dataURL.indexOf(contentType) === -1) {
      throw new Error('Mismatch in data URL and content type');
    }
    const { uploadURL, s3URL, message } = await getSignedUrlFromS3(
      contentType,
      fileName,
      moduleName,
      getCurrentUser().companyId
    );
    if (message) {
      throw new Error(message);
    }
    const urlPrefix = new RegExp(`^data:${contentType};base64,`);
    const _buffer = buffer.Buffer.from(
      dataURL.replace(urlPrefix, ''),
      'base64'
    );
    await axios.put(uploadURL, _buffer, {
      headers: {
        'Content-Type': contentType,
        'Content-Encoding': 'base64'
      }
    });
    return s3URL;
  } catch (uploadBase64ImageErr) {
    return Promise.reject(uploadBase64ImageErr);
  }
};

const uploadFile = async (
  fileObj,
  moduleEntityId,
  moduleName,
  companyId = getCurrentUser().companyId
) => {
  try {
    if (!moduleEntityId || !moduleName || !companyId) {
      throw new Error('Mandatory fields missing');
    }

    if (!fileObj || !(fileObj instanceof File)) {
      throw new Error('Not an instance file');
    }
    const { name: fileName, type: fileType } = fileObj;
    if (!fileName || !fileType) {
      throw new Error('File type not supported');
    }
    const imgCompressionPreference = await LocalPreferenceService.getByKey(
      'moduleId',
      MODULE_IDS.SETTINGS
    );
    const compressionValue = imgCompressionPreference[0]?.value;
    if (fileType.includes('image') && compressionValue) {
      const initialQuality = 1 - compressionValue / 100;
      const options = {
        initialQuality: initialQuality, // Lower = higher compression
        maxSizeMB: 10, // Optional: max final file size
        maxWidthOrHeight: 2560, // Optional: scale down large images
        useWebWorker: true,
        alwaysKeepResolution: true
      };
      const result = await imageCompression(fileObj, options);
      if (result) {
        fileObj = result;
      }
    }
    const { uploadURL, s3URL, message } = await getSignedUrlFromS3(
      fileType,
      `${moduleEntityId}_${fileName}`,
      moduleName,
      getCurrentUser().companyId
    );
    if (message) {
      throw new Error(message);
    }
    await axios.put(uploadURL, fileObj, {
      headers: {
        'Content-Type': fileType
      }
    });
    return s3URL;
  } catch (uploadBase64ImageErr) {
    return Promise.reject(uploadBase64ImageErr);
  }
};

export { getSignedUrlFromS3, uploadBase64DataURL, uploadFile };
